import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Alert from 'flarum/common/components/Alert';
import icon from 'flarum/common/helpers/icon';
import listItems from 'flarum/common/helpers/listItems';
import IndexPage from 'flarum/forum/components/IndexPage';
import WelcomeHero from 'flarum/forum/components/WelcomeHero';
import FillInput from '../../common/components/FillInput';

interface QuestionData {
  type: string; // 'single' | 'multiple' | 'fill' | 'open'
  content: string;
  options: Array<{ text: string }> | null;
  settings: Record<string, any> | null;
}

export default class QuestionnairePage extends Page<{ routeParams: { id: string } }> {
  oninit(vnode: any) {
    super.oninit(vnode);

    this.questionnaireId = m.route.param('id');
    this.loading = true;
    this.submitting = false;

    // Questionnaire metadata
    this.questionnaire = null;
    app.history.push('questionnairePage', app.translator.trans('tohsakarat-questionnaire.forum.list.title'));
    this.setTitle();

    // Questions returned by GET /qwn-questionnaires/:id
    this.questions = [] as QuestionData[];
    this.isResumed = false;

    // User's answers: { [questionIndex]: any }
    this.answers = {} as Record<string, any>;

    // Result state
    this.submitError = '';
    this.submitted = false;
    this.finalScore = null;
    this.triggeredActions = [];

    this.loadQuestionnaire();
  }

  loadQuestionnaire() {
    this.loading = true;

    // Step 1: load questionnaire metadata from store
    const stored = app.store.getById('qwn-questionnaires', this.questionnaireId);
    if (stored) {
      this.questionnaire = stored;
    }

    // Step 2: fetch the attempt-ready payload from GET /qwn-questionnaires/:id
    app
      .request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/qwn-questionnaires/${this.questionnaireId}`,
      })
      .then((response: any) => {
        app.store.pushPayload(response);
        this.questionnaire = app.store.getById('qwn-questionnaires', this.questionnaireId);
        
        const data = response.data?.attributes || {};
        this.questions = data.questions || [];
        this.isResumed = !!data.isResumed;
        this.loading = false;
        this.setTitle();
        m.redraw();

        if (this.isResumed) {
          app.alerts.show({ type: 'success' }, '检测到您有未提交的答卷，已为您恢复断点续答进度。');
        }
      })
      .catch((err: any) => {
        this.loading = false;
        const detail = err?.response?.errors?.[0]?.detail;
        let errorMessage = '';
        if (err.status === 403) {
            errorMessage = detail || app.translator.trans('tohsakarat-questionnaire.forum.page.permission_denied');
        } else if (err.status === 422) {
            errorMessage = detail || '无法生成问卷，请重试或联系管理员';
        } else {
            errorMessage = detail || (app.translator.trans('tohsakarat-questionnaire.forum.page.load_error') as string);
        }
        app.alerts.show({ type: 'error' }, errorMessage);
        m.redraw();
      });
  }

  oncreate(vnode: any) {
    super.oncreate(vnode);
    this.setTitle();
  }

  setTitle() {
    let title = app.translator.trans('tohsakarat-questionnaire.forum.list.title');
    
    if (this.questionnaire) {
        title = this.questionnaire.title();
    }
    
    app.setTitle(title);
    app.setTitleCount(0);
  }

  view() {
    const title = this.questionnaire ? this.questionnaire.title() : `#${this.questionnaireId}`;
    const coverUrl = this.questionnaire && this.questionnaire.cover_url ? this.questionnaire.cover_url() : null;
    const instructions = this.questionnaire && this.questionnaire.instructions ? this.questionnaire.instructions() : '';

    return (
      <div className="IndexPage IndexPage--questionnaire-attempt">
        {coverUrl ? (
          <header className="Hero qwn-questionnaire-hero has-cover">
            <img src={coverUrl} alt={title} className="qwn-questionnaire-hero__bg" />
            <div className="container qwn-questionnaire-hero__content">
              <div className="containerNarrow">
                <h1 className="Hero-title qwn-questionnaire-hero__title">{title}</h1>
              </div>
            </div>
          </header>
        ) : (
          <header className="Hero qwn-questionnaire-hero">
            <div className="container">
              <div className="containerNarrow">
                <h1 className="Hero-title">{title}</h1>
              </div>
            </div>
          </header>
        )}
        <div className="container">
          <div className="sideNavContainer">
            <nav className="IndexPage-nav sideNav">
              <ul>{listItems(IndexPage.prototype.sidebarItems().toArray())}</ul>
            </nav>
            <div className="IndexPage-results sideNavOffset">
              <div className="qwn-attempt-page">
                <div className="qwn-attempt-page__header">
                  <a
                    className="qwn-attempt-page__back-link"
                    href={app.route('questionnaires')}
                    onclick={(e: Event) => {
                      e.preventDefault();
                      m.route.set(app.route('questionnaires'));
                    }}
                  >
                    {icon('fas fa-arrow-left')}
                    {app.translator.trans('tohsakarat-questionnaire.forum.page.back_btn')}
                  </a>
                </div>

                {instructions && !this.submitted && (
                  <div className="qwn-attempt-page__instructions">
                    {icon('fas fa-info-circle')}
                    <span>{instructions}</span>
                  </div>
                )}

                {this.loading ? (
                  <div className="qwn-attempt-page__loading">
                    <LoadingIndicator />
                  </div>
                ) : this.questionnaire?.attribute('isPassed') ? (
                  this.renderAlreadyPassedState()
                ) : this.submitted ? (
                  this.renderSubmittedState()
                ) : (
                  this.renderForm()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderForm() {
    return (
      <div className="qwn-attempt-form">
        <div className="qwn-questions-list">
          {this.questions.map((question: QuestionData, index: number) =>
            this.renderQuestion(question, index)
          )}
        </div>

        {this.questions.length > 0 && (
          <div className="qwn-attempt-form__submit">
            {Button.component(
              {
                className: 'Button Button--primary',
                loading: this.submitting,
                onclick: () => this.onsubmit(),
              },
              app.translator.trans('tohsakarat-questionnaire.forum.page.submit_btn')
            )}
          </div>
        )}
      </div>
    );
  }

  renderQuestion(question: QuestionData, index: number) {
    const qid = String(index); // use array index as ID
    const typeLabel = this.getTypeLabel(question.type);

    return (
      <div key={qid} className="qwn-question-item">
        <div className="qwn-question-item__header">
          <span className="qwn-question-item__index">{index + 1}.</span>
          <span className="qwn-question-item__type-badge">{typeLabel}</span>
        </div>
        <div className="qwn-question-item__content">{question.content}</div>
        <div className="qwn-question-item__input">
          {question.type === 'single' && this.renderSingleChoice(question, qid)}
          {question.type === 'multiple' && this.renderMultipleChoice(question, qid)}
          {question.type === 'fill' && this.renderFill(question, qid)}
          {question.type === 'open' && this.renderOpen(question, qid)}
          {question.type === 'slider' && this.renderSlider(question, qid)}
        </div>
      </div>
    );
  }

  renderSingleChoice(question: QuestionData, qid: string) {
    const currentAnswer = this.answers[qid] as number[] | undefined;

    return (
      <div className="qwn-options-list">
        {(question.options || []).map((opt: { text: string }, optIndex: number) => (
          <label key={String(optIndex)} className="qwn-option-label">
            <input
              type="radio"
              name={`q-${qid}`}
              checked={Array.isArray(currentAnswer) && currentAnswer.includes(optIndex)}
              onchange={() => {
                this.answers[qid] = [optIndex];
              }}
            />
            <span className="qwn-option-label__text">{opt.text}</span>
          </label>
        ))}
      </div>
    );
  }

  renderMultipleChoice(question: QuestionData, qid: string) {
    if (!this.answers[qid]) {
      this.answers[qid] = [];
    }
    const currentAnswer = this.answers[qid] as number[];

    return (
      <div className="qwn-options-list">
        {(question.options || []).map((opt: { text: string }, optIndex: number) => (
          <label key={String(optIndex)} className="qwn-option-label">
            <input
              type="checkbox"
              checked={currentAnswer.includes(optIndex)}
              onchange={(e: Event) => {
                const checked = (e.target as HTMLInputElement).checked;
                if (checked) {
                  this.answers[qid] = [...currentAnswer, optIndex];
                } else {
                  this.answers[qid] = currentAnswer.filter((id: number) => id !== optIndex);
                }
              }}
            />
            <span className="qwn-option-label__text">{opt.text}</span>
          </label>
        ))}
      </div>
    );
  }

  renderFill(question: QuestionData, qid: string) {
    const blankCount = question.settings?.fill_types?.length || 1;
    if (!this.answers[qid]) {
      this.answers[qid] = Array(blankCount).fill('');
    }
    const currentAnswer = this.answers[qid] as string[];

    return (
      <div className="qwn-fill-list">
        {Array.from({ length: blankCount }).map((_, i: number) => {
          const fillType = question.settings?.fill_types?.[i] || 'any';
          return (
            <div key={String(i)} className="qwn-fill-item">
              <span className="qwn-fill-item__label">
                {app.translator.trans('tohsakarat-questionnaire.forum.page.blank_label', { index: i + 1 })}
              </span>
              <FillInput
                fillType={fillType}
                value={currentAnswer[i] || ''}
                oninput={(val: string) => {
                  const newArr = [...currentAnswer];
                  newArr[i] = val;
                  this.answers[qid] = newArr;
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  renderOpen(question: QuestionData, qid: string) {
    if (!this.answers[qid]) {
      this.answers[qid] = '';
    }
    const minLen = question.settings?.min_length || 0;

    return (
      <div className="qwn-open-input">
        <textarea
          className="FormControl"
          rows={5}
          value={this.answers[qid] as string}
          oninput={(e: Event) => {
            this.answers[qid] = (e.target as HTMLTextAreaElement).value;
          }}
          placeholder={
            minLen
              ? (app.translator.trans('tohsakarat-questionnaire.forum.page.open_placeholder_min', { min: minLen }) as string)
              : (app.translator.trans('tohsakarat-questionnaire.forum.page.open_placeholder') as string)
          }
        />
        {minLen > 0 && (
          <p className="qwn-open-input__hint">
            {app.translator.trans('tohsakarat-questionnaire.forum.page.open_min_hint', { min: minLen })}
          </p>
        )}
      </div>
    );
  }

  renderSlider(question: QuestionData, qid: string) {
    const min = question.settings?.min ?? 0;
    const max = question.settings?.max ?? 10;
    const step = question.settings?.step ?? 1;

    if (this.answers[qid] === undefined) {
      this.answers[qid] = min;
    }

    const currentVal = this.answers[qid];
    const percent = ((currentVal - min) / (max - min)) * 100;

    return (
      <div className="qwn-slider-container">
        <div className="qwn-slider-track-box">
          <span className="qwn-slider-limit qwn-slider-limit--min">{min}</span>
          <div className="qwn-slider-input-wrapper">
            <div className="qwn-slider-value-preview-container" style={{ '--qwn-slider-percent': `${percent}%` }}>
              <span className="qwn-slider-value-preview">{currentVal}</span>
            </div>
            <input
              type="range"
              className="qwn-slider-input"
              min={min}
              max={max}
              step={step}
              value={currentVal}
              oninput={(e: Event) => {
                const val = parseInt((e.target as HTMLInputElement).value, 10);
                this.answers[qid] = val;
              }}
            />
          </div>
          <span className="qwn-slider-limit qwn-slider-limit--max">{max}</span>
        </div>
      </div>
    );
  }

  renderSubmittedState() {
    const htmlActions = this.triggeredActions.filter((a: any) => a.type === 'html_prompt' || a.action === 'html_prompt');

    return (
      <div className="qwn-attempt-result">
        <div className="qwn-attempt-result__icon">
          {icon('fas fa-check-circle')}
        </div>
        <h2 className="qwn-attempt-result__title">
          {app.translator.trans('tohsakarat-questionnaire.forum.page.submitted_title')}
        </h2>
        <p className="qwn-attempt-result__desc">
          {app.translator.trans('tohsakarat-questionnaire.forum.page.submitted_desc')}
        </p>

        {htmlActions.length > 0 && (
          <div className="qwn-attempt-result__custom-html qwn-attempt-result__custom-html--spaced">
            {htmlActions.map((act: any, i: number) => {
              const rawHtml = act.payload?.html || '';
              return (
                <div className="qwn-custom-html-window" key={i}>
                  <div className="qwn-custom-html-window__header">
                    <span className="qwn-custom-html-window__title">
                      {icon('fas fa-info-circle')}
                      {app.translator.trans('tohsakarat-questionnaire.forum.page.html_prompt_title', {}, '提示信息')}
                    </span>
                  </div>
                  <div className="qwn-custom-html-window__body Post-body">
                    {m.trust(rawHtml)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <br />
        <a
          className="Button Button--primary"
          href={app.route('index')}
          onclick={(e: Event) => {
            e.preventDefault();
            m.route.set(app.route('index'));
          }}
        >
          {app.translator.trans('core.forum.header.back_to_index_tooltip')}
        </a>
      </div>
    );
  }

  renderAlreadyPassedState() {
    return (
      <div className="qwn-attempt-result">
        <div className="qwn-attempt-result__icon qwn-attempt-result__icon--success">
          {icon('fas fa-medal')}
        </div>
        <h2 className="qwn-attempt-result__title">
          已通过身份校验
        </h2>
        <p className="qwn-attempt-result__desc">
          系统检测到您此前已成功满足此类测试的通关所需资格（答题得分或前置权限达标），无需重复挑战。可以直接去论坛自由探索了！
        </p>

        <br />
        <a
          className="Button Button--primary"
          href={app.route('index')}
          onclick={(e: Event) => {
            e.preventDefault();
            m.route.set(app.route('index'));
          }}
        >
          {app.translator.trans('core.forum.header.back_to_index_tooltip')}
        </a>
      </div>
    );
  }

  onsubmit() {
    const validationError = this.validate();
    if (validationError) {
        let message = '';
        const index = validationError.index + 1;
        
        switch (validationError.error) {
            case 'unanswered':
                message = app.translator.trans('tohsakarat-questionnaire.forum.page.validation_unanswered') as string;
                break;
            case 'too_short':
                message = app.translator.trans('tohsakarat-questionnaire.forum.page.validation_open_too_short', {
                    index, 
                    min: validationError.min, 
                    actual: validationError.actual
                }) as string;
                break;
            case 'too_long':
                message = app.translator.trans('tohsakarat-questionnaire.forum.page.validation_open_too_long', {
                    index, 
                    max: validationError.max, 
                    actual: validationError.actual
                }) as string;
                break;
        }
        
        app.alerts.show({ type: 'error' }, message);
        return;
    }

    this.submitting = true;
    this.submitError = '';

    const payload = {
      answers: this.answers,
    };

    app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/qwn-questionnaires/${this.questionnaireId}/attempts`,
        body: { data: { attributes: payload } },
      })
      .then((response: any) => {
        this.submitting = false;
        this.submitted = true;
        if (response.data && response.data.attributes) {
            if (response.data.attributes.score !== undefined) {
                this.finalScore = response.data.attributes.score;
            }
            const snapshot = response.data.attributes.snapshot_data || {};
            this.triggeredActions = snapshot.triggered_actions || [];
        }
        m.redraw();
      })
      .catch((err: any) => {
        this.submitting = false;
        const detail = err?.response?.errors?.[0]?.detail || '';
        const errorMessage = detail || (app.translator.trans('tohsakarat-questionnaire.forum.page.submit_error') as string);
        app.alerts.show({ type: 'error' }, errorMessage);
        m.redraw();
      });
  }

  getTypeLabel(type: string): string {
    const map: Record<string, string> = {
      single: app.translator.trans('tohsakarat-questionnaire.forum.page.type_single') as string,
      multiple: app.translator.trans('tohsakarat-questionnaire.forum.page.type_multiple') as string,
      fill: app.translator.trans('tohsakarat-questionnaire.forum.page.type_fill') as string,
      open: app.translator.trans('tohsakarat-questionnaire.forum.page.type_open') as string,
      slider: app.translator.trans('tohsakarat-questionnaire.forum.page.type_slider') as string,
    };
    return map[type] || type;
  }

  validate(): { error: string; index: number; min?: number; max?: number; actual?: number } | null {
    for (let i = 0; i < this.questions.length; i++) {
        const q = this.questions[i];
        const ans = this.answers[String(i)];

        if (q.type === 'single' || q.type === 'multiple') {
            if (!ans || !Array.isArray(ans) || ans.length === 0) {
                return { error: 'unanswered', index: i };
            }
        } else if (q.type === 'fill') {
            const blankCount = q.settings?.fill_types?.length || 1;
            if (!ans || !Array.isArray(ans) || ans.length < blankCount) {
                return { error: 'unanswered', index: i };
            }
            if (ans.some(val => !val || String(val).trim() === '')) {
                return { error: 'unanswered', index: i };
            }
        } else if (q.type === 'open') {
            const min = q.settings?.min_length || 0;
            const max = q.settings?.max_length || 0;
            const content = (ans || '').trim();
            
            if (content === '') {
                return { error: 'unanswered', index: i };
            }
            if (min > 0 && content.length < min) {
                return { error: 'too_short', index: i, min, actual: content.length };
            }
            if (max > 0 && content.length > max) {
                return { error: 'too_long', index: i, max, actual: content.length };
            }
        }
    }
    return null;
  }
}
